/** JSON shapes for alert detail payloads and similar UI parsing. */
export type JsonPrimitive = string | number | boolean | null;

export interface JsonObject {
  [key: string]: JsonValue;
}

export type JsonValue = JsonPrimitive | JsonValue[] | JsonObject;

export function isJsonObject(value: JsonValue | undefined): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Coerce a JSON value to string or null when present; undefined when absent or invalid. */
export function optionalNullableString(
  value: JsonValue | undefined
): string | null | undefined {
  if (value === undefined) return undefined;
  if (value === null) return null;
  return typeof value === "string" ? value : undefined;
}

/** Read a required string field from a JSON object. */
export function requiredString(obj: JsonObject, key: string): string | null {
  const value = obj[key];
  return typeof value === "string" ? value : null;
}

function isJsonValue(value: JsonPrimitive | JsonObject | JsonValue[]): value is JsonValue {
  if (value === null) return true;
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.every((entry) => isJsonValue(entry));
  }
  return Object.values(value).every((entry) => isJsonValue(entry));
}

function narrowJsonValue(value: JsonPrimitive | JsonObject | JsonValue[]): value is JsonValue {
  return isJsonValue(value);
}

/** Parse a JSON string into a validated JSON value, or null on invalid input. */
export function parseJsonValue(raw: string): JsonValue | null {
  try {
    const parsed: JsonPrimitive | JsonObject | JsonValue[] = JSON.parse(raw);
    return narrowJsonValue(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Parse a JSON string into an object map, or null when not a JSON object. */
export function parseJsonObject(raw: string): JsonObject | null {
  const parsed = parseJsonValue(raw);
  return parsed !== null && isJsonObject(parsed) ? parsed : null;
}

/**
 * Parse a Python-style ``repr`` dict (single-quoted) into an object.
 * Used for legacy tool-call alert details stored via ``str(output)``.
 */
export function parseLooseObjectString(raw: string): JsonObject | null {
  const trimmed = raw.trim();
  if (!trimmed.startsWith("{") || !trimmed.endsWith("}")) return null;

  const normalized = trimmed
    .replace(/\bTrue\b/g, "true")
    .replace(/\bFalse\b/g, "false")
    .replace(/\bNone\b/g, "null")
    .replace(/'/g, '"');

  return parseJsonObject(normalized);
}

/** Parse alert ``detail`` as JSON or a loose object string. */
export function parseAlertDetailObject(raw?: string | null): JsonObject | null {
  if (!raw) return null;
  return parseJsonObject(raw) ?? parseLooseObjectString(raw);
}

/** Parse a JSON string into an array of JSON values, or null when not a JSON array. */
export function parseJsonArray(raw: string): JsonValue[] | null {
  const parsed = parseJsonValue(raw);
  return parsed !== null && Array.isArray(parsed) ? parsed : null;
}

export function isEmptyJsonValue(value: JsonValue): boolean {
  if (value === null || value === "") return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isJsonObject(value)) return Object.keys(value).length === 0;
  return false;
}
